import type { GameAdConfig, GameAdFormat, GameAdFrequencyPolicy, GameAdVariant } from './types';

function hashBucket(seed: string): number {
  // FNV-1a keeps assignment synchronous and deterministic in browsers and tests.
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % 100;
}

export function getGameAdVariant(input: {
  userId: string;
  experimentId: string;
  rolloutPercent: number;
}): GameAdVariant {
  const rolloutPercent = Math.max(0, Math.min(100, Math.trunc(input.rolloutPercent)));
  if (rolloutPercent === 0) return 'control';
  if (rolloutPercent === 100) return 'treatment';
  return hashBucket(`${input.experimentId}:cohort:${input.userId}`) < rolloutPercent
    ? 'treatment'
    : 'control';
}

export function shouldShowGameAdOpportunity(input: {
  userId: string;
  experimentId: string;
  sessionId: string;
  format: Exclude<GameAdFormat, 'none'>;
  opportunityKey: string;
  aggression: number;
}): boolean {
  const aggression = Math.max(0, Math.min(100, Math.trunc(input.aggression)));
  if (aggression === 0) return false;
  if (aggression === 100) return true;
  const seed = [
    input.experimentId,
    input.userId,
    input.sessionId,
    input.format,
    input.opportunityKey,
  ].join(':');
  return hashBucket(seed) < aggression;
}

export function getGameAdFrequencyPolicy(config: GameAdConfig): GameAdFrequencyPolicy {
  return {
    everyAnsweredQuestions: Math.max(3, config.interstitialQuestionInterval ?? 3),
    cooldownSeconds: Math.max(60, config.interstitialCooldownSeconds ?? 60),
    displayRatePercent: Math.max(0, Math.min(100, config.aggression)),
  };
}

export function isGameAdsEnabled(config: GameAdConfig, variant: GameAdVariant): boolean {
  return (
    config.mode === 'test' &&
    config.rolloutPercent > 0 &&
    config.aggression > 0 &&
    variant === 'treatment' &&
    (config.interstitialEnabled || config.rewardedEnabled || config.bannerEnabled)
  );
}
